import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListEventSubscribersResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Setup > Developer > Events (F-127): subscribers, their replay position and
 * their lag - "a silently lagging consumer looks identical to a working one".
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function EventStreamPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-127');
  const { can } = useSession();
  const allowed = enabled.enabled && can('setup.modules.manage');
  const list = useQuery<ListEventSubscribersResult, ApiError>({ queryKey: ['event-subscribers'], queryFn: () => api.listEventSubscribers({}), enabled: allowed });
  const [name, setName] = useState('');
  const [events, setEvents] = useState('deal.*');
  const [fromStart, setFromStart] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['event-subscribers'] });
  const create = useMutation({
    mutationFn: () => api.createEventSubscriber({ body: { name, events: events.split(',').map((e) => e.trim()).filter(Boolean), fromStart } }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });
  const replay = useMutation({
    mutationFn: (subscriberId: string) => api.replayEvents({ path: { subscriberId }, body: { fromSeq: 0 } }),
    onSuccess: refresh,
  });
  if (!allowed) return null;
  return (
    <Panel data-region="event-stream">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('stream.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('stream.help')}</p>
        {list.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : list.isError ? (
          <p className="text-dense text-danger-text">{errorText(list.error)}</p>
        ) : (
          <ul className="flex flex-col">
            {list.data.subscribers.map((sub) => (
              <li key={sub.subscriberId} data-subscriber={sub.name} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-2 first:border-t-0">
                <span className="text-dense text-ink">{sub.name}</span>
                <span className="text-meta text-ink-meta">{sub.events.join(', ')}</span>
                <span data-subscriber-lag className={sub.behind > 0 ? 'text-meta text-warning-text' : 'text-meta text-success-text'}>
                  {sub.behind > 0
                    ? t('stream.behind', { values: { count: formatCount(sub.behind, format), minutes: formatCount(Math.round((sub.oldestUnackedSeconds ?? 0) / 60), format) } })
                    : t('stream.caughtUp')}
                </span>
                <span className="text-meta text-ink-meta">
                  {t('stream.position', { values: { cursor: formatCount(sub.cursor, format), latest: formatCount(sub.latestSeq, format) } })}
                  {sub.lastPolledAt ? ` · ${t('stream.polled', { values: { at: formatDateTime(sub.lastPolledAt, format) } })}` : ` · ${t('stream.neverPolled')}`}
                </span>
                <Button intent="ghost" size="small" data-subscriber-replay className="ms-auto" disabled={replay.isPending} onClick={() => replay.mutate(sub.subscriberId)}>
                  {t('stream.replayAll')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input data-subscriber-name aria-label={t('stream.name')} placeholder={t('stream.name')} className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
          <input data-subscriber-events aria-label={t('stream.events')} className={INPUT} value={events} onChange={(e) => setEvents(e.target.value)} />
          <label className="flex items-center gap-1 text-dense text-ink">
            <input type="checkbox" data-subscriber-from-start checked={fromStart} onChange={(e) => setFromStart(e.target.checked)} />
            {t('stream.fromStart')}
          </label>
          <Button intent="secondary" size="small" data-subscriber-create disabled={create.isPending} onClick={() => create.mutate()}>
            {t('stream.add')}
          </Button>
        </div>
        {create.error ? <p data-subscriber-refusal className="text-dense text-danger-text">{errorText(create.error)}</p> : null}
      </div>
    </Panel>
  );
}
